import { Shield, Layers, Sparkles, Scale, Wallet, GitMerge } from 'lucide-react';

const accentStyles = {
  cyan: {
    laneBorder: 'border-cyan-500',
    laneBg: { dark: 'bg-cyan-500/10', light: 'bg-cyan-50' },
    icon: { dark: 'text-cyan-400', light: 'text-cyan-600' },
    iconBg: { dark: 'bg-cyan-500/15 border-cyan-500/30', light: 'bg-cyan-100 border-cyan-200' },
    cardBorder: { dark: 'border-slate-800 hover:border-cyan-500/40', light: 'border-slate-200 hover:border-cyan-300' },
    cardBg: { dark: 'bg-slate-950/80', light: 'bg-white' },
    spine: { dark: 'bg-cyan-500/30', light: 'bg-cyan-200' },
    hub: { dark: 'border-cyan-500/40 bg-slate-900 text-cyan-300', light: 'border-cyan-200 bg-white text-cyan-700' },
  },
  amber: {
    laneBorder: 'border-amber-500',
    laneBg: { dark: 'bg-amber-500/10', light: 'bg-amber-50' },
    icon: { dark: 'text-amber-400', light: 'text-amber-600' },
    iconBg: { dark: 'bg-amber-500/15 border-amber-500/30', light: 'bg-amber-100 border-amber-200' },
    cardBorder: { dark: 'border-slate-800 hover:border-amber-500/40', light: 'border-slate-200 hover:border-amber-300' },
    cardBg: { dark: 'bg-slate-950/80', light: 'bg-white' },
    spine: { dark: 'bg-amber-500/30', light: 'bg-amber-200' },
    hub: { dark: 'border-amber-500/40 bg-slate-900 text-amber-300', light: 'border-amber-200 bg-white text-amber-700' },
  },
  indigo: {
    laneBorder: 'border-indigo-500',
    laneBg: { dark: 'bg-indigo-500/10', light: 'bg-indigo-50' },
    icon: { dark: 'text-indigo-400', light: 'text-indigo-600' },
    iconBg: { dark: 'bg-indigo-500/15 border-indigo-500/30', light: 'bg-indigo-100 border-indigo-200' },
    cardBorder: { dark: 'border-slate-800 hover:border-indigo-500/40', light: 'border-slate-200 hover:border-indigo-300' },
    cardBg: { dark: 'bg-slate-950/80', light: 'bg-white' },
    spine: { dark: 'bg-indigo-500/30', light: 'bg-indigo-200' },
    hub: { dark: 'border-indigo-500/40 bg-slate-900 text-indigo-300', light: 'border-indigo-200 bg-white text-indigo-700' },
  },
};

export const engagementIcons = {
  shield: Shield,
  layers: Layers,
  sparkles: Sparkles,
  scale: Scale,
  wallet: Wallet,
  merge: GitMerge,
};

const EngagementTriggerMap = ({ lanes, theme, variant = 'cyan', hubLabel = 'Leadership inflection points' }) => {
  const isDark = theme === 'dark';
  const mode = isDark ? 'dark' : 'light';
  const accent = accentStyles[variant] || accentStyles.cyan;

  return (
    <div className="relative">
      {/* Hub connector — desktop */}
      <div className="hidden lg:block relative mb-10">
        <div className={`absolute top-1/2 left-[16.67%] right-[16.67%] h-px -translate-y-1/2 ${accent.spine[mode]}`} />
        <div className="flex justify-center">
          <div
            className={`relative z-10 px-6 py-2.5 rounded-full border text-xs uppercase tracking-[0.25em] font-semibold ${accent.hub[mode]}`}
          >
            {hubLabel}
          </div>
        </div>
        <div className="flex mt-4">
          {lanes.map((lane) => (
            <div key={lane.label} className="flex-1 flex justify-center">
              <div className={`w-px h-8 ${accent.spine[mode]}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 lg:gap-5">
        {lanes.map((lane) => {
          const Icon = engagementIcons[lane.icon] || Shield;

          return (
            <div
              key={lane.label}
              className={`flex flex-col rounded-2xl border overflow-hidden ${
                isDark ? 'border-slate-800' : 'border-slate-200 shadow-sm'
              }`}
            >
              <div className={`border-t-4 ${accent.laneBorder} ${accent.laneBg[mode]} px-6 py-5`}>
                <div className="flex items-start gap-4">
                  <div
                    className={`flex-shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${accent.iconBg[mode]}`}
                  >
                    <Icon className={`w-5 h-5 ${accent.icon[mode]}`} aria-hidden />
                  </div>
                  <div>
                    <h3 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {lane.label}
                    </h3>
                    {lane.description && (
                      <p className={`mt-1 text-sm leading-snug ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {lane.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className={`flex-1 p-4 space-y-3 ${isDark ? 'bg-slate-900/30' : 'bg-slate-50/50'}`}>
                {lane.triggers.map((trigger) => (
                  <div
                    key={trigger}
                    className={`group relative p-4 rounded-xl border transition-all duration-300 ${accent.cardBorder[mode]} ${accent.cardBg[mode]}`}
                  >
                    <div
                      className={`absolute left-0 top-4 bottom-4 w-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity ${accent.laneBorder.replace('border-', 'bg-')}`}
                    />
                    <p className={`text-sm leading-relaxed pl-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                      {trigger}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EngagementTriggerMap;
